/**
 * Doctors list – helper functions (use i18n at runtime)
 */

import { t } from 'i18next';
import { GetApiDoctorsPortalDoctorsStatus as DoctorStatusEnum } from '@/shared/api/models/getApiDoctorsPortalDoctorsStatus';
import type { ConnectionStatusOption, StatusDateRow } from '../components/doctorsList.types';
import { FILTER_ALL_VALUE } from '../components/doctorsList.types';

export function buildConnectionStatusOptions(): ConnectionStatusOption[] {
    return [
        { value: FILTER_ALL_VALUE, label: t('Common.All') },
        { value: DoctorStatusEnum.LINKED, label: t('Doctors.Linked') },
        { value: DoctorStatusEnum.INVITED, label: t('Doctors.StatusInvited') },
        { value: DoctorStatusEnum.REMOVED, label: t('Doctors.StatusRemoved') },
        { value: DoctorStatusEnum.REJECTED, label: t('Doctors.StatusRejected') },
        { value: DoctorStatusEnum.SELF_UNLINKED, label: t('Doctors.StatusSelfUnlinked') },
    ];
}

export function getConnectionStatusLabel(status: string | undefined): string {
    if (!status) return t('Doctors.NotLinked');
    const map: Record<string, string> = {
        [DoctorStatusEnum.LINKED]: t('Doctors.Linked'),
        [DoctorStatusEnum.INVITED]: t('Doctors.StatusInvited'),
        [DoctorStatusEnum.REMOVED]: t('Doctors.StatusRemoved'),
        [DoctorStatusEnum.REJECTED]: t('Doctors.StatusRejected'),
        [DoctorStatusEnum.SELF_UNLINKED]: t('Doctors.StatusSelfUnlinked'),
    };
    return map[status] ?? status;
}

export function getConnectionStatusBadgeStyle(status: string | undefined): { color: string; bg: string } {
    switch (status) {
        case DoctorStatusEnum.LINKED:
            return { color: '#3b9a45', bg: '#ecf5ed' };
        case DoctorStatusEnum.INVITED:
            return { color: '#b45309', bg: '#fff2e8' };
        case DoctorStatusEnum.REMOVED:
        case DoctorStatusEnum.REJECTED:
            return { color: '#991b1b', bg: '#ffe5e5' };
        case DoctorStatusEnum.SELF_UNLINKED:
            return { color: '#6b7280', bg: '#f3f4f6' };
        default:
            return { color: '#6b7280', bg: '#f3f4f6' };
    }
}

export function getStatusDateLabel(status: string | undefined): string | null {
    switch (status) {
        case DoctorStatusEnum.LINKED:
            return t('Doctors.LinkedDate');
        case DoctorStatusEnum.REMOVED:
            return t('Doctors.RemovedAt');
        case DoctorStatusEnum.SELF_UNLINKED:
            return t('Doctors.SelfUnlinkedAt');
        case DoctorStatusEnum.REJECTED:
            return t('Doctors.RejectedAt');
        case DoctorStatusEnum.INVITED:
            return t('Doctors.InvitedAt');
        default:
            return null;
    }
}

export function getStatusDateValue(row: StatusDateRow): string | null {
    const status = row.status;
    if (status === DoctorStatusEnum.INVITED) return row.invitedAt ?? null;
    if (status === DoctorStatusEnum.LINKED) return row.linkedAt ?? null;
    if (status === DoctorStatusEnum.REJECTED) return row.rejectedAt ?? null;
    if (status === DoctorStatusEnum.REMOVED || status === DoctorStatusEnum.SELF_UNLINKED) return row.unlinkedAt ?? null;
    return null;
}
